import { reference } from 'components/form/fields/utils/dt-formats';
import { getPlaceholder } from 'components/form/utils/input-utils';
import { determineBasedOnWidth } from 'components/form/utils/task-utils';
import { Indices } from 'contexts/FieldInfoContext';
import { FormInfo } from 'contexts/FormInfoContext';
import parseISO from 'date-fns/parseISO';
import { TraitHelper } from 'helpers/traits';
import _ from 'lodash';
import { FieldInfo, FieldRenderType } from 'types/field';
import { Values, ValueProps, Field, FieldType, MultipleProps } from 'types/graphql';
import { TaskRendering } from 'types/theming';
import Types from 'types/types';
import { getPath, isEmptyValue } from 'utils/utils';

export const fieldMinWidth = 235

export function getDefaultValue(fieldType: FieldRenderType) {
  switch (fieldType) {
    case "TEXT":
    case "DECIMAL":
    case "INTEGER":
      return ""

    case "MULTIPLE SELECT":
    case "CHECKBOX SELECT":
      return []

    case "SINGLE SELECT":
    case "SINGLE SELECT SUBMIT":
    case "SEGMENTED BOOLEAN BUTTON":
    case "RADIO SELECT":
    case "RADIO BOOLEAN SELECT":
      return null

    case 'MULTIPLE':
      return [{}]

    case 'DATETIME':
    case 'TIME':
    case 'DATE':
      return null

    default:
      return ""
  }
}

function getValue(props: any) {
  const value = props.augProps.value
  if (value !== null && value !== undefined) {
    return value
  }

  return getDefaultValue(props.info.type)
}

export async function focusOnInputByClassName(className: string) {
  const elements = document.querySelectorAll(`.${className} input`)
    
  if (elements.length > 0){
    // @ts-ignore
    window.setTimeout(() => elements[0].focus(), 0);
  }
}

export async function focusOnField(rpath: string) {
  const element = document.getElementById(rpath);
  if (element){
    window.setTimeout(() => element.focus(), 0);
  } else {
    console.error("Could not focus on field with id: %o", rpath)
  }
}

// get all properties relevant to the rendering of an input field
export const toFieldProps = (props: any) => {
  const { t, augProps, info } = props
  const isEmpty               = (msg: string) => isEmptyValue(msg) || (_.isString(msg) && msg.trim().length === 0)
  const msg                   = augProps.error
  const helperText            = info.inMultiple ? undefined : isEmpty(msg) ? ' ' : msg // value ' ' creates a whitespace line for the helpertext component

  const inputFieldProps = info.field.type === "MULTIPLE" ? {} : {
    // tabIndex      : augProps.meta ? augProps.meta.tabindex : -1,
    onChange      : augProps.handleChange,
    onBlur        : augProps.handleBlur,
    onFocus       : augProps.handleFocus,
    placeholder   : getPlaceholder(t, info ? info.type : augProps.type),
    ...augProps.inputProps,
  }

  return {
    id            : info.rpath,
    name          : info.rpath,
    value         : getValue(props),
    required      : !Boolean(info.field.optional),
    error         : Boolean(augProps.isError()),
    helperText    : helperText,
    ...inputFieldProps
  }
}

export function toValueType(value: any) {
  if (!isNaN(parseInt(value)))
    return "INTEGER"
  else if (!isNaN(parseFloat(value)))
    return "DECIMAL"
  else
    return "TEXT"
}

export function getField(formInfo: FormInfo, fieldPath: string): Field {
  const fields = formInfo.form.fields
  const field  = getPath(fields, fieldPath)

  if (!field) {
    console.error("Could not find input field with path: %o", fieldPath)
    return undefined as unknown as Field
  }

  return field
}

export function getValues(formInfo: FormInfo, valuesPath: string): ValueProps {
  return getPath(formInfo.form.values, valuesPath) as ValueProps
}

export function toRuntimeValue(formValue: any, type: FieldType): string | null {
  switch(type) {
    case "DECIMAL":
      return isEmptyValue(formValue) ? null : formValue.trim().replace(',', '.')
    case "DATE":
      try {
        return isEmptyValue(formValue) ? null : reference.date.format(formValue)
      } catch (error) {
        return null
      }
    case "TIME":
      try {
        return isEmptyValue(formValue) ? null : reference.time.format(formValue)
      } catch (error) {
        return null
      }
    case "DATETIME":
      try {
        if (formValue instanceof Date)
          return formValue.toISOString()
      } catch (error) {
        return null
      }
      return null
    default:
      return formValue
  }
}

export function toComponentValue(fieldValue: string, type: FieldType): any {
  switch(type) {
    case "DATE":
      try {
        return isEmptyValue(fieldValue) ? null : reference.date.parse(fieldValue)
      } catch (error) {
        return null
      }
    case "TIME":
      try {
        return isEmptyValue(fieldValue) ? null : reference.time.parse(fieldValue)
      } catch (error) {
        return null
      }
    case "DATETIME":
      try {
        return isEmptyValue(fieldValue) ? null : parseISO(fieldValue)
      } catch (error) {
        return null
      }
    default:
      return fieldValue
  }
}

export function toFieldRenderType(formInfo: FormInfo, field: Field, taskRendering: TaskRendering): FieldRenderType {
  const path  = field.path
  const rpath = replaceIndexesWith(path, "[0]")
  const vals  = getValues(formInfo, toValuesPath(formInfo, rpath))
  const hasOptions        = field.hasChoices
  const inMultiple        = path.includes('.')
  const isDependingSelect = field.dependants?.length > 0 && hasOptions
  const numOptions        = vals?.numChoices || 0
  const forceSelect       = TraitHelper.hasTrait(field, "select")
  const isSelect          = inMultiple || numOptions < 2 || numOptions > 3 || isDependingSelect || forceSelect
  const isEntity          = (numOptions > 0 && vals?.choices && Boolean(vals?.choices[0].tuple)) || (field.labelType === 'tuple')

  switch (field.type) {
    case "MULTIPLE FILE": 
      return field.type as FieldRenderType

    case "MULTIPLE":
      return field.type

    case field.type.match(/MULTIPLE.*/)?.input:
      return isSelect
        ? (isEntity ? "MULTIPLE ENTITY SELECT" : "MULTIPLE SELECT")
        : "CHECKBOX SELECT"

    case "ENTITY":
      return isEntity ? "SINGLE ENTITY SELECT" : "SINGLE SELECT"

    case "SELECT":
    case (hasOptions || isDependingSelect) && field.type:
      const hasSubmitTrait = TraitHelper.hasTrait(field, 'submit')
      const isSubmitField = Types.isSubmitField(formInfo, field) 
      if (hasSubmitTrait && !isSubmitField)
        console.error("Cannot render %o as a submit button.", rpath)

      return isSubmitField
        ? "SINGLE SELECT SUBMIT"
        : (isSelect ?  "SINGLE SELECT" : "RADIO SELECT")

    case "BOOLEAN":
      if (inMultiple) 
        return taskRendering == 'standard' ? "SEGMENTED BOOLEAN BUTTON" : "SINGLE SELECT"
      else
        return "RADIO BOOLEAN SELECT"

    default:
      return field.type as FieldRenderType
  }
}

export function replaceIndexesWith(path: string, replacement: string) {
  return path.replace(/\[[^\[\]]*\]/g, replacement);
}

export function toFieldInfoPath(path: string) {
  // example:
  // one[2].two[3].tree => one.fields.two.fields.tree
  return path.replace(/\./g, ".fields.").replace(/\[[^\[\]]*\]/g, "");
}

export function toFormFieldInfo(formInfo: FormInfo, field: Field, indices: Indices, taskRendering: TaskRendering): FieldInfo {
  const rpath       = toValuePath(field.path, indices)
  const valsPath    = toValuesPath(formInfo, rpath)
  const fieldPath   = toFieldPath(formInfo, rpath)
  const verifyField = getField(formInfo, fieldPath)
  const values      = getValues(formInfo, valsPath)
  const type        = toFieldRenderType(formInfo, field, taskRendering)

  if (verifyField?.path != field.path)
    console.error("There is a critical issue in creating field info.")
  
  return {
    field:        field,
    type:         type,

    indices:      indices,
    rpath:        rpath,
    auxPath:      valsPath,
    fieldPath:    fieldPath,

    hasOptions:   field.hasChoices,
    options:      values?.choices,
    optionsKind:  values?.choicesKind,
    numOptions:   values?.numChoices || 0,

    inMultiple:   rpath.includes('.'),
    required:     !!field?.optional,
    isDepending:  field.dependants?.length > 0,
  }
}

export function toValuePath(path: string, indices: Indices): string {
  return path.replace(/\$\w+/g, (matched: string): string => {
    const name = matched.substring(1)
    const index = indices[name]
    return index.toString()
  });
}

// build a field path based on formInfo
export function toFieldPath(formInfo: FormInfo, path: string) {
  function buildPath(fields: Field[] | undefined, parts: any[]): string[] {
    if (!parts.length)
      return []

    const current = parts.shift()
    if (current && fields) {
      const name    = replaceIndexesWith(current, "")
      const index   = fields.findIndex(field => field.name == name)
      const field   = fields[index]
      const fpath   = `[${index}]` + (parts.length > 0 ? ".fields" : "")
      return [fpath, ...buildPath(field.fields, parts)]
    } else
      return []
  }

  try {
    // in case this is a "values" path (i.e. where we can look either in "rows" or the "templateRow") turn it back into a "normal" path
    const normalizedPath = path.replace(".rows", "").replace(".templateRow", "[0]")
    return buildPath(formInfo.form.fields, normalizedPath.split(".")).join(".")
  } catch (e) {
    console.error("Could not create field path for path: %o", path)
    throw e
  }
}

export function enrichValues(values: any, additionalValues: any) {
  const obj: any = {}
  for (const key in values) {
    const value = values[key]
    if (Array.isArray(value)) {
      const additionalValueArray = additionalValues[key]
      if (!additionalValueArray) {
        obj[key] = value
      }
      else if (additionalValueArray.length > 0 && value.length == 0) {
        obj[key] = additionalValueArray
      }
      else {
        obj[key] = value.map((element, index) => enrichValues(element, additionalValueArray[index]))
      }
    } else {
      obj[key] = value
    }
  }
  for (const key in additionalValues) {
    const value = additionalValues[key]
    if (Array.isArray(value)) continue
    obj[key] = value
  }
  return obj
}

export function collectAdditionalFromSaved(values: any) {
  const obj: any = {}
  for (const key in values) {
    const value = values[key]
    if (key.startsWith("__")) {
      obj[key] = value
    } else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      obj[key] = value.map((elem: any) => collectAdditionalFromSaved(elem))
    }
  }
  return obj
}

export function collectAdditionalValues(values: any) {
  function isMultiple(value: any): boolean {
    return "templateRow" in value && "rows" in value
  }

  const obj: any = {}
  for (const key in values) {
    const value = values[key]
    // starting with __ indicates an additional value, e.g. __basedOn
    if (key.startsWith("__")) {
      obj[key] = value
    } else if (isMultiple(value)) {
      if (value.rows.length == 0) {
        const templateAdditional = collectAdditionalValues(value.templateRow)
        if (templateAdditional.length > 0) obj[key] = [templateAdditional]
      } else {
        obj[key] = value.rows.map((elem: any) => collectAdditionalValues(elem))
      }
    }
  }
  return obj
}

export function collectDefaults(obj: Values | ValueProps | MultipleProps, path: string, formInfo: FormInfo, useDynamicDefault: boolean): any {
  function collectDefaultsFromProps(props: ValueProps | MultipleProps, vpath: string): any {
    const field = getField(formInfo, toFieldPath(formInfo, vpath))

    // case: value is a ValueProps
    if ("default" in props || "numChoices" in props) {
      const required = !field.optional
      // if field is required, has no set default, and has only one choice, automatically select that choice
      if (required && isEmptyValue(props.default) && (props.numChoices || 0) == 1) {
        return field.type.startsWith("MULTIPLE")
          ? [props.choices![0]]
          : props.choices![0]
      }
      else return props.default
    } // case: value is a MultipleProps
    else if ("rows" in props) {
      const rows = (useDynamicDefault && props.rows.length > 0) || field.mode === 'closed'
        ? props.rows
        : [props.templateRow]
  
      const subPath = (index: any) => vpath + "[" + index + "]"
  
      return rows.map((row, index) => collectDefaultsFromValues(row, subPath(index)))
    }
    return undefined
  }

  function collectDefaultsFromValues(values: Values, path: string): any {
    const obj: any = {}
    for (const key in values) {
      if (key.startsWith("__")) continue
      const vpath = path ? path + "." + key : key
      const value = values[key]
      const defVal = collectDefaultsFromProps(value, vpath)
      if (defVal !== undefined) obj[key] = defVal
    }
    return obj
  }

  if ("default" in obj || "numChoices" in obj || "rows" in obj) {
    return collectDefaultsFromProps(obj, path)
  }
  // @ts-ignore
  else return collectDefaultsFromValues(obj, path)
}

// build a value path based on formInfo
export function toValuesPath(formInfo: FormInfo, rpath: string) {
  function insertRows(indexPath: string): string {
    const parts = indexPath.split("[")
    if (parts.length == 1) return indexPath
    else return parts[0] + ".rows[" + parts[1]
  }

  function buildPath(fields: Field[] | undefined, parts: any[] ): string[] {
    if (!parts.length)
      return []

    const current = parts.shift()
    const name    = replaceIndexesWith(current, "")
    const field   = fields?.find(field => field.name == name)
    if (field?.type === "MULTIPLE")
      return field?.mode === "open"
        ? [replaceIndexesWith(current, ".templateRow"), ...buildPath(field.fields, parts)]
        : [insertRows(current), ...buildPath(field.fields, parts)]
    else
      return [current, ...buildPath(field?.fields, parts)]
  }

  try {
    return buildPath(formInfo.form.fields, rpath.split(".").filter(x => x)).join(".")
  } catch (e) {
    console.error("Could not create field path for path: %o", rpath)
    throw e
  }
}

export const fieldMinWidthStyle = (formInfo: FormInfo, field: Field, taskRendering: TaskRendering) => ({ minWidth: toFieldMinWidth(formInfo, field, taskRendering).toString() + "px" })

export function toFieldMinWidth(formInfo: FormInfo, field: Field, taskRendering: TaskRendering): number {
  const type  = toFieldRenderType(formInfo, field, taskRendering)
  const width = determineFieldWidth(formInfo, field, type, taskRendering)
  return width
}

export function determineFieldWidth(formInfo: FormInfo, field: Field, type: FieldRenderType, taskRendering: TaskRendering): number {

  switch (type) {
    case "MULTIPLE":

      // compute width of field columns
      // TODO: might need to replace [0] with templateRow
      const basedOnPath  = replaceIndexesWith(field.path, "[0]") + "[0].__basedOn"
      const basedOn      = _.get(formInfo.form.values, basedOnPath)
      const basedOnWidth = determineBasedOnWidth(basedOn, true)

      const widthPerField  = field?.fields?.map(subfield => toFieldMinWidth(formInfo, subfield, taskRendering)) || []
      // @ts-ignore
      const fieldsWidth: number    = widthPerField.reduce((a,b) => a+b, 0)

      // compute width of action columns
      const actionWidth = field.mode == "closed" ? 0 : 170

      return fieldsWidth + basedOnWidth + actionWidth

    case "TEXT":                     return 235
    case "FILE":                     return 235
    case "MULTIPLE SELECT":          return 235
    case "SINGLE SELECT":            return 235
    case "SEGMENTED BOOLEAN BUTTON": return 0
    case 'DATETIME':                 return 220
    case 'TIME':                     return 130
    case 'DATE':                     return 150
    case 'INTEGER':                  return 100
    case 'DECIMAL':                  return 100
    case 'PERIOD':                   return 0
    default:                         return 235

  }
}




